import { RouteGuard } from '@/common/RouteGuard';
import { checkViewClient, checkViewDashboard, checkViewOperator, checkViewRole } from '@/hooks/PermissionHook';
import { isKey } from '@/utils/variable';
import path from 'path';

const listPathNoneLeftMenu: Array<RegExp> = [/login/, /signup/, /forgot-password/];
const patternPath: string = '/:path*';

export { listPathNoneLeftMenu };

export const ROUTES = {
  login: {
    path: '/login',
  },

  signup: {
    path: '/signup',
  },

  forgotPassword: {
    path: '/forgot-password',
  },

  newPassword: {
    path: '/new-password',
  },

  root: {
    path: '/',
    guard: {
      requireAuth: true,
      checkPermission: checkViewDashboard,
    } as RouteGuard,
  },
  account: {
    path: '/account',
    signin: {
      path: '/account/signin',
      guard: {
        requireNotAuth: true,
        redirect: '/',
      } as RouteGuard,
    },
    signout: {
      path: '/account/signout',
      guard: {
        requireAuth: true,
        redirect: '/',
      } as RouteGuard,
    },
    signup: {
      path: '/account/signup',
      guard: {
        requireNotAuth: true,
        redirect: '/',
      } as RouteGuard,
    },
    forgotPassword: {
      path: '/account/forgot-password',
      guard: {
        requireNotAuth: true,
        redirect: '/',
      } as RouteGuard,
    },
    resetPassword: {
      path: '/account/reset-password',
      guard: {
        requireNotAuth: true,
        redirect: '/',
      } as RouteGuard,
    },
    confirmAccount: {
      path: '/account/confirm-account',
    },
  },
  orders: {
    path: '/orders',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },
  warehouse: {
    path: '/warehouse',
    products: {
      path: '/warehouse/products',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
    categories: {
      path: '/warehouse/categories',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
  },
  clients: {
    path: '/clients',
    guard: {
      requireAuth: true,
      checkPermission: checkViewClient,
    } as RouteGuard,
  },
  systems: {
    path: '/systems',
    users: {
      path: '/systems/users',
      guard: {
        requireAuth: true,
        checkPermission: checkViewOperator,
      } as RouteGuard,
    },
    roles: {
      path: '/systems/roles',
      guard: {
        requireAuth: true,
        checkPermission: checkViewRole,
      } as RouteGuard,
    },
  },

  settings: {
    path: '/settings',
    profile: {
      path: '/settings/profile',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
    changePassword: {
      path: '/settings/change-password',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
  },

  purchaseOrder: {
    path: '/purchase-order',
    guard: {
      requireAuth: true,
    } as RouteGuard,
    create: {
      path: '/purchase-order/create-new-po',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
  },

  purchase_detail: {
    path: '/purchase-order/:id',
    getPath: (id: string) => `/purchase-order/${id}`,
  },

  bankAccount: {
    path: '/bank-accounts',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  importBankStatement: {
    path: '/import-bank-statement',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  myTransaction: {
    path: '/my-transactions',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  contactDetail: {
    path: '/contacts/:id',
    getPath: (id: string) => `/contacts/${id}`,
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  // financial settings
  financialSettings: {
    path: '/financial-settings',
    guard: {
      requireAuth: true,
    } as RouteGuard,
    chartOfAccounts: {
      path: '/financial-settings/chart-of-accounts',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
    bsob: {
      path: '/financial-settings/bsob',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
    generalJournal: {
      path: '/financial-settings/general-journal',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
    buPu: {
      path: '/financial-settings/bu-pu',
      guard: {
        requireAuth: true,
      } as RouteGuard,
    },
  },

  gst: {
    path: '/financial-settings/general-journal',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  gstCreate: {
    path: '/financial-settings/general-journal/create',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  gstEdit: {
    path: '/financial-settings/general-journal/:id',
    getPath: (id?: string) => (id ? `/financial-settings/general-journal/${id}` : '/404'),
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  publicQuote: {
    path: '/public/quotes',
  },

  publicQuoteGeneratePdf: {
    path: '/public/quotes-generate-pdf',
  },

  publicBillsToPay: {
    path: '/public/bills-to-pay',
  },

  publicBillsToPayGeneratePdf: {
    path: '/public/bills-to-pay-generate-pdf',
  },

  publicInvoices: {
    path: '/public/invoices',
  },

  publicInvoicesGeneratePdf: {
    path: '/public/invoices-generate-pdf',
  },

  publicPurchaseOrder: {
    path: '/public/purchase-order',
  },

  publicPurchaseOrderGeneratePdf: {
    path: '/public/purchase-order-generate-pdf',
  },

  invoices: {
    path: '/invoices',
  },

  quotes: {
    path: '/quotes',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  contacts: {
    path: '/contacts',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  projects: {
    path: '/projects',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  fileManagement: {
    path: '/file-management',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  financialReports: {
    path: '/financial-reports',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  billsToPay: {
    path: '/bills-to-pay',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  accountReceivableStatement: {
    path: '/account-receivable-statement',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  accountPayableStatement: {
    path: '/account-payable-statement',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  cashReceipt: {
    path: '/cash-receipt',
    guard: {
      requireAuth: true,
    } as RouteGuard,
  },

  invoice_detail: {
    path: '/invoices/:id',
    getPath: (id: string) => `/invoices/${id}`,
  },

  product: {
    path: '/products-and-services',
  },

  product_detail: {
    path: '/products-and-services/:id',
    getPath: (id: string) => `/products-and-services/${id}`,
  },

  quote_detail: {
    path: '/quotes/:id',
    getPath: (id: string) => `/quotes/${id}`,
  },

  bills_to_pay_detail: {
    path: '/bills-to-pay/:id',
    getPath: (id: string) => `/bills-to-pay/${id}`,
  },

  project_detail: {
    path: '/projects/:id',
    getPath: (id: string) => `/projects/${id}`,
  },
};

export const NONAUTH_ROUTES = [
  ROUTES.login.path,
  // ROUTES.signup.path,
  ROUTES.forgotPassword.path,
  ROUTES.newPassword.path,
];

export const PUBLIC_ROUTES = [
  ROUTES.signup.path,
  ROUTES.publicQuote.path,
  ROUTES.publicQuoteGeneratePdf.path,
  ROUTES.publicBillsToPay.path,
  ROUTES.publicBillsToPayGeneratePdf.path,
  ROUTES.publicInvoices.path,
  ROUTES.publicInvoicesGeneratePdf.path,
  ROUTES.publicPurchaseOrder.path,
  ROUTES.publicPurchaseOrderGeneratePdf.path,
];

export function returnListRouteCheck(): Array<string> {
  let arrayMatcher: Array<string> = [];
  Object.keys(ROUTES).map(key => {
    if (key && key != 'root' && isKey(ROUTES, key)) {
      const stgPats: string = ROUTES[key].path + patternPath;
      arrayMatcher.push(stgPats);
    }
  });
  return arrayMatcher;
}
